import React, { FC, memo, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { PreviewPlayerContext } from 'contexts/PreviewPlayerContext'
import { NWShortCutContext } from 'contexts/NWShortCutContext'
import type Konva from 'konva'
import { createImage } from '../features/konva-shapes/createImage'

export const PreviewPlayerContextProvider: FC = memo(({ children }) => {
  const [ canvasSource, setCanvasSource ] = useState<HTMLCanvasElement | undefined>(undefined)
  const [ imageSource, setImageSource ] = useState<HTMLImageElement | undefined>(undefined)
  const [ videoSource, setVideoSource ] = useState<HTMLVideoElement | undefined>(undefined)

  const [ previewPlayer, setPreviewPlayer ] = useState<HTMLDivElement | null>(null)
  const previewPlayerBox = useRef<HTMLDivElement | null>(null)

  const onSetPreviewPlayer: React.RefCallback<HTMLDivElement> = useCallback(ref => {
    setPreviewPlayer(ref)
  }, [ setPreviewPlayer ])

  const { registerNWGlobalHotKey, unRegisterNWGlobalHotKey } = useContext(NWShortCutContext)

  const handleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen()
      unRegisterNWGlobalHotKey()
    } else if (previewPlayer) {
      previewPlayer.requestFullscreen()
      registerNWGlobalHotKey()
    }
  }, [ registerNWGlobalHotKey, unRegisterNWGlobalHotKey, previewPlayer ])

  const saveFrame = (data: string, fileName = 'frame') => {
    const link = document.createElement('a')
    link.href = data
    link.download = fileName
    link.click()
    URL.revokeObjectURL(link.href)
    link.remove()
  }

  const captureFrame = useCallback((isMedia: boolean) => {
    if (isMedia) {
      if (videoSource) {
        const canvas = document.createElement('canvas')
        canvas.width = videoSource.videoWidth
        canvas.height = videoSource.videoHeight
        const ctx = canvas.getContext('2d')

        if (ctx) {
          ctx.drawImage(videoSource, 0, 0, canvas.width, canvas.height)
          window.requestAnimationFrame(() => {
            saveFrame(canvas.toDataURL('image/jpeg', 0.9), 'source-video-frame.jpg')
            canvas.remove()
          })
        }
      }

      // if (imageSource?.src) {
      //   fetch(imageSource.src)
      //     .then(response => response.blob())
      //     .then(blob => new Promise<string>((resolve, reject) => {
      //       const reader = new FileReader()
      //       reader.onloadend = () => resolve(reader.result as string)
      //       reader.onerror = reject
      //       reader.readAsDataURL(blob)
      //     }))
      //     .then(data => saveFrame(data, 'image-source.jpeg'))
      // }
      if (imageSource) {
        const size = {
          width: imageSource.naturalWidth,
          height: imageSource.naturalHeight,
        }
        createImage(imageSource.src, { size, opacity: 1, outlineWidth: undefined, outlineColor: undefined })
          .then((shape: Konva.Image) => {
            saveFrame(shape.toDataURL({ mimeType: 'image/jpeg', quality: 0.9 }), 'image-source.jpeg')
          })
      }
    }

    if (!isMedia && canvasSource) {
      saveFrame(canvasSource.toDataURL('image/jpeg', 0.9), 'timeline-frame.jpeg')
    }
  }, [ videoSource, canvasSource, imageSource ])

  const values = useMemo(() => ({
    previewPlayer,
    previewPlayerBox,
    onSetPreviewPlayer,
    imageSource,
    canvasSource,
    videoSource,
    setImageSource,
    setCanvasSource,
    setVideoSource,
    handleFullscreen,
    captureFrame,
  }), [
    previewPlayer,
    previewPlayerBox,
    onSetPreviewPlayer,
    imageSource,
    canvasSource,
    videoSource,
    setImageSource,
    setCanvasSource,
    setVideoSource,
    handleFullscreen,
    captureFrame,
  ])

  return (
    <PreviewPlayerContext.Provider value={values}>
      {children}
    </PreviewPlayerContext.Provider>
  )
})
